import { z } from "zod";
import { canonicalResultValueSchema } from "./canonical-result.js";
import { runAnswerEvidenceReferenceSchema } from "./run-answer-evidence.js";
const id = z.string().trim().min(1).max(200);
const slug = z.string().regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/u).max(120);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/u);
const column = z.string().trim().min(1).max(240);
/** A metric identity is independent of the model's chosen result alias. */
export const twinAnswerTestMeasureSchema = z.object({
    field: z.string().regex(/^[a-z][a-z0-9_]*$/u).nullable(),
    aggregation: z.enum(["count_rows", "count_non_null", "count_distinct", "sum", "average", "minimum", "maximum"]),
    basisSha256: sha256,
    interpretation: z.enum(["stored_source_value", "declared_measure", "product_row_count"]),
}).strict().refine(value => value.aggregation === "count_rows"
    ? value.field === null && value.interpretation === "product_row_count"
    : value.field !== null && value.interpretation !== "product_row_count", "A row count and a field metric require different source identities.");
export const twinAnswerTestOracleSchema = z.object({
    runId: id, certificateId: id, certificateSha256: sha256, resultEvidenceSha256: sha256,
    rowIndex: z.number().int().min(0).max(9_999), column,
}).strict();
/** Expectations come from a separate retained query, never from the tested answer. */
export const twinAnswerTestFactSchema = z.object({
    id: slug,
    label: z.string().trim().min(1).max(240),
    resultColumns: z.array(column).min(1).max(8).optional(),
    resultMeasure: twinAnswerTestMeasureSchema.optional(),
    rowSelector: z.record(column, canonicalResultValueSchema).refine(value => Object.keys(value).length <= 16, "At most 16 selector fields are allowed."),
    oracle: twinAnswerTestOracleSchema,
    // Rounding is a declared display choice. Underlying values must still agree exactly.
    fractionDigits: z.number().int().min(0).max(100),
}).strict().superRefine((fact, context) => {
    if (Boolean(fact.resultColumns) === Boolean(fact.resultMeasure)) {
        context.addIssue({ code: "custom", message: "Select exactly one result locator: columns or a governed measure." });
    }
    if (fact.resultColumns && new Set(fact.resultColumns).size !== fact.resultColumns.length) {
        context.addIssue({ code: "custom", path: ["resultColumns"], message: "Result columns must be unique." });
    }
});
export const twinAnswerTestCaseSchema = z.object({
    id: slug, prompt: z.string().min(1).max(8_000),
    facts: z.array(twinAnswerTestFactSchema).min(1).max(200),
    reviewCriteria: z.array(z.string().trim().min(1).max(2_000)).max(20),
}).strict().superRefine((value, context) => {
    if (new Set(value.facts.map(f => f.id)).size !== value.facts.length) {
        context.addIssue({ code: "custom", path: ["facts"], message: "Fact IDs must be unique within a case." });
    }
});
export const twinAnswerTestSuiteSchema = z.object({
    schemaVersion: z.literal("mlx.twin-answer-tests.v1"),
    id: slug, version: z.number().int().positive(),
    name: z.string().trim().min(1).max(240),
    description: z.string().trim().min(1).max(2_000),
    productVersionId: z.string().regex(/^tl_pver_[0-9a-f]{32}$/u), productSha256: sha256,
    scope: z.literal("declared_cited_numeric_facts"),
    cases: z.array(twinAnswerTestCaseSchema).min(1).max(100),
}).strict().superRefine((value, context) => {
    if (value.productVersionId !== `tl_pver_${value.productSha256.slice(0, 32)}`) {
        context.addIssue({ code: "custom", message: "Test suite must bind the exact product digest." });
    }
    if (new Set(value.cases.map(c => c.id)).size !== value.cases.length) {
        context.addIssue({ code: "custom", path: ["cases"], message: "Case IDs must be unique." });
    }
    const facts = value.cases.flatMap(c => c.facts);
    if (facts.length > 512 || new Set(facts.map(f => `${f.oracle.runId}:${f.oracle.certificateId}`)).size > 32) {
        context.addIssue({ code: "custom", path: ["cases"], message: "A suite may contain at most 512 facts from 32 source queries." });
    }
});
export const twinAnswerTestPublicationSchema = z.object({
    id: z.string().regex(/^tats_[0-9a-f]{32}$/u),
    tenantSlug: id, organisationId: id, suiteSha256: sha256,
    suite: twinAnswerTestSuiteSchema,
    publishedBy: id, publishedAt: z.number().int().nonnegative(),
}).strict();
export const twinAnswerTestFactResultSchema = z.object({
    factId: slug, status: z.enum(["passed", "failed", "inconclusive"]),
    reason: z.enum(["values_verified", "value_mismatch", "display_mismatch", "no_matching_citation",
        "ambiguous_result_row", "incomplete_result", "display_not_numeric", "numeric_source_required"]),
    oracle: twinAnswerTestOracleSchema,
    citations: z.array(z.object({
        certificateId: id, certificateSha256: sha256, rowIndex: z.number().int().nonnegative(), column,
        jsonPointer: runAnswerEvidenceReferenceSchema.shape.jsonPointer,
        start: z.number().int().nonnegative(), end: z.number().int().nonnegative(),
    }).strict().refine(value => value.end > value.start, "Citation offsets must select text.")).max(256),
}).strict().superRefine((value, context) => {
    const failure = value.reason === "value_mismatch" || value.reason === "display_mismatch";
    if (value.status !== (failure ? "failed" : value.reason === "values_verified" ? "passed" : "inconclusive") ||
        value.status !== "inconclusive" && value.citations.length === 0) {
        context.addIssue({ code: "custom", message: "Fact outcome must agree with its evidence and reason." });
    }
});
export const twinAnswerTestAuthoritySchema = z.object({
    publicationId: z.string().regex(/^tats_[0-9a-f]{32}$/u), suiteSha256: sha256,
    caseId: slug, scope: z.literal("declared_cited_numeric_facts"),
    facts: z.array(twinAnswerTestFactResultSchema).min(1).max(200),
}).strict().superRefine((value, context) => {
    if (new Set(value.facts.map(fact => fact.factId)).size !== value.facts.length) {
        context.addIssue({ code: "custom", message: "Evaluated fact IDs must be unique." });
    }
});
